/*----------------------------------------------------------------------------*\
  #MODALS
\*----------------------------------------------------------------------------*/

// Imports
import { $overlay, openOverlay, closeOverlay } from './overlay';
import { createEl } from 'util/create-el';
import { trapFocus, restoreFocus, getFocusableElements } from './trap-focus';

const Modals = (options) => {
  // Private: Default settings object
  const _defaults = {
    selector: '.js-modalElement',
    modalsSelector: '.js-modals',
    $selectors() {
      return document.querySelectorAll(this.selector);
    },
    exists() {
      return this.$selectors().length > 0;
    },
    $openModals() {
      return document.querySelectorAll(`${this.selector}.${this.openClass}`);
    },
    hasOpenModals() {
      return this.$openModals().length > 0;
    },
    multipleOpenModals() {
      return this.$openModals().length > 1;
    },
    $modals() {
      return document.querySelector(this.modalsSelector);
    },
    $closeButtons() {
      return document.querySelectorAll('.js-closeModal');
    },
    openClass: 'is-open',
  };

  // Private: Merge passed in object with defaults
  const _settings = {
    ..._defaults,
    ...options,
  };

  const _$trapEls = () => {
    return [
      document.querySelector('.c-app__head'),
      document.querySelector('.c-app__main'),
      document.querySelector('.c-app__aside'),
      ..._settings.$selectors(),
    ];
  };

  const _$currentOpenModal = () =>
    Array.from(
      document.querySelectorAll(`${_settings.selector}.${_settings.openClass}`)
    ).pop();

  const _closeModals = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.detail.open) return;

    restoreFocus(_$trapEls());
    $overlay().removeEventListener('overlay:transitionstart', _closeModals);

    _settings.$modals().setAttribute('aria-hidden', 'true');
    _settings.$modals().classList.remove(_settings.openClass);
    document.body.classList.remove(`${_settings.openClass}Modals`);

    _settings.$selectors().forEach(($modal) => {
      $modal.style.display = 'none';
      $modal.setAttribute('aria-hidden', 'true');
      $modal.dispatchEvent(new CustomEvent('modal:close'));
    });
  };

  const _offsetOpenModals = () => {
    if (!_settings.hasOpenModals()) return;

    if (_settings.multipleOpenModals()) {
      _offsetModals(_settings.$openModals());
    } else {
      _settings.$openModals()[0].removeAttribute('style');
    }
  };

  const _removeOpenCurrentState = ($modal) => {
    if (!$modal) return;

    $modal.setAttribute('aria-hidden', 'true');
    $modal.classList.remove('is-current');
  };

  const _setOpenCurrentState = ($modal) => {
    if (!$modal) return;

    $modal.setAttribute('aria-hidden', 'false');
    $modal.classList.add('is-current');
  };

  const _offsetModals = ($modals) => {
    const $modalsReversed = Array.from($modals).reverse();
    $modalsReversed.forEach(($modal, i) => {
      $modal.removeAttribute('style');
      const scale = (100 - i * 5) / 100;

      if (scale < 1) {
        $modal.style.cssText = `transform: scale(${scale}) translateY(${i}rem)`;
      }
    });
  };

  const _closeModalHandler = (event) => {
    event.preventDefault();

    const $modal = event.currentTarget.closest(_settings.selector);
    _closeModal($modal);
  };

  const _closeModal = ($modal) => {
    _removeOpenCurrentState($modal);
    $modal.classList.remove(_settings.openClass);
    $modal.dispatchEvent(new CustomEvent('modal:close'));
    _trapFocusToModal();

    if (_settings.$openModals().length > 0) {
      _offsetOpenModals();
      _focusNextElement();

      _settings
        .$openModals()
        .forEach(($modal) => _removeOpenCurrentState($modal));
      _setOpenCurrentState(_$currentOpenModal());
    } else {
      closeOverlay();
    }
  };

  const _focusNextElement = () => {
    getFocusableElements(_$currentOpenModal())[0].focus();
  };

  const _trapFocusToModal = () => {
    if (_settings.hasOpenModals()) {
      trapFocus(_$trapEls());
      restoreFocus([_$currentOpenModal()]);
    } else {
      restoreFocus(_$trapEls());
    }
  };

  const _closeCurrentModal = () => {
    if (!_settings.hasOpenModals()) return;
    _closeModal(_$currentOpenModal());
  };

  // Closes every open modal at once, regardless of how many are stacked --
  // for navigating out of the whole prompt stack into a different flow (e.g.
  // "Continue" on the pending-signature prompt opening the document viewer,
  // see site-docs.js) rather than dismissing one card and revealing the next
  // (which is what _closeModal/js-closeModal does). Listened for as a DOM
  // event rather than exposed on this module's returned API, since
  // site-docs.js is a separate module with no reference to this instance.
  //
  // This is deliberately global -- it closes every .js-modalElement on the
  // page, not just the dashboard's pending-signature/induction stack. That's
  // safe today only because the app's other .js-modalElement consumer,
  // _otp_modal.html.erb, renders in a mutually-exclusive branch of
  // dashboard/show.html.erb (staff/admin roles, never alongside an
  // operative's pending-signature prompt). If a third .js-modalElement
  // consumer is ever added that COULD coexist with the dashboard prompt
  // stack, this event would also force-close that one -- scope this (e.g. to
  // a specific container) before relying on it in that situation.
  const _closeAllModals = () => {
    if (!_settings.hasOpenModals()) return;

    restoreFocus(_$trapEls());

    _settings.$modals().setAttribute('aria-hidden', 'true');
    _settings.$modals().classList.remove(_settings.openClass);
    document.body.classList.remove(`${_settings.openClass}Modals`);

    _settings.$selectors().forEach(($modal) => {
      $modal.classList.remove(_settings.openClass);
      _removeOpenCurrentState($modal);
      $modal.dispatchEvent(new CustomEvent('modal:close'));
    });

    closeOverlay();
  };

  const _keyUpHandler = (event) => {
    if (event.key === 'Escape') _closeCurrentModal();
  };

  const _activate = ($modal) => {
    // Modals already marked is-open server-side (e.g. pending inductions)
    // get their dimmed overlay from a server-rendered @overlay_active —
    // this is a no-op for those. Modals opened on demand via JS (Smart
    // Check, upload progress) have no such server state and need this to
    // ever show an overlay at all. It also matters for cleanup: closing
    // relies on the overlay's opacity actually transitioning to fire the
    // event the rest of the teardown is wired to — if it was never
    // opened, closeOverlay() changes nothing, no transition fires, and
    // the modal never finishes closing (stuck is-openModals body class).
    openOverlay();

    if ($modal) $modal.classList.add(_settings.openClass);

    const $container = _settings.$modals();
    if ($container) {
      $container.classList.add(_settings.openClass);
      $container.setAttribute('aria-hidden', 'false');
    }

    document.body.classList.add(`${_settings.openClass}Modals`);
    _offsetOpenModals();
    _setOpenCurrentState(_$currentOpenModal());
    _trapFocusToModal();

    const $current = _$currentOpenModal();
    const focusable = $current ? getFocusableElements($current) : [];
    if (focusable.length > 0) focusable[0].focus();
  };

  // Public: Open a specific modal on demand — for modals that aren't
  // already marked is-open server-side (e.g. opened by a button click)
  const open = (target) => {
    const $modal =
      typeof target === 'string' ? document.querySelector(target) : target;

    if (!$modal) return;

    _activate($modal);
  };

  // Public: Destroy module instance
  const destroy = () => {
    if (!_settings.exists()) return;

    $overlay().removeEventListener('overlay:transitionstart', _closeModals);
    $overlay().removeEventListener('overlay:transitionend', _closeModals);
    document.removeEventListener('keyup', _keyUpHandler);
    document.removeEventListener('modals:closeAll', _closeAllModals);

    _settings.$closeButtons().forEach(($button) => {
      $button.removeEventListener('click', _closeModalHandler);
    });
  };

  // Public: Destroy module instance and run initialise again
  const reinit = () => {
    destroy();
    init();
  };

  // Public: Initialise module
  const init = () => {
    if (!_settings.exists()) return;

    $overlay().addEventListener('overlay:transitionstart', _closeModals);
    $overlay().addEventListener('overlay:transitionend', _closeModals);

    document.addEventListener('keyup', _keyUpHandler);
    document.addEventListener('modals:closeAll', _closeAllModals);

    _settings.$closeButtons().forEach(($button) => {
      $button.addEventListener('click', _closeModalHandler);
    });

    if (_settings.hasOpenModals()) _activate();
  };

  // Return public methods
  return {
    destroy,
    reinit,
    init,
    open,
  };
};

// Default module export
export default Modals;
